import {closeCurrentDialog, openDialog, DialogsContext} from 'platform/components';
import {Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {use, useState} from 'react';

import {filter, find, isNil, isNotNil, reject, sortBy} from 'ramda';
import {isEmptyArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  IntegrationActionKey,
  ServiceOrderIntegration,
  useGetServiceOrderIntegrationsQuery,
  usePutServiceOrderIntegrationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {useServiceCaseDetailActions} from '@dms/shared-aftersales';

import {Nullish, TestIdProps, openInNewTab, suffixTestId} from 'shared/web';

import {IntegrationPopper} from '../../../../../components/IntegrationPopper/IntegrationPopper';
import {MAZDA_CLAIM_SERVICE_CODE} from '../constants/mazdaClaimServiceCode';
import {MAZDA_PORTAL_INTEGRATION_CODE} from '../constants/mazdaPortalIntegrationCode';
import {MAZDA_PREAUTHORIZATION_SERVICE_CODE} from '../constants/mazdaPreauthorizationServiceCode';
import {AudatexCalculations} from './AudatexCalculations';
import {ClaraDetailDialog} from './ClaraDetailDialog';
import {ClaraSendDialog} from './ClaraSendDialog';
import {HyundaiAs1qRetrieveDialog} from './HyundaiAs1qRetrieveDialog';
import {IntegrationItem} from './IntegrationItem';
import {ORDER_INTEGRATION_ACTION_PREFIX} from './IntegrationsDropdown/constants/actionKeys';
import {MazdaChecklistChooseDialog} from './MazdaChecklistChooseDialog';
import {MazdaClaimDetailDialog} from './MazdaClaimDetailDialog';
import {MazdaClaimSendDialog} from './MazdaClaimSendDialog';
import {MazdaPreauthDetailDialog} from './MazdaPreauthDetailDialog';
import {MazdaPreauthSendDialog} from './MazdaPreauthSendDialog';
import {OrderTrackedTime} from './OrderTrackedTime';
import {SilverdatCalculations} from './SilverdatCalculations';

const isIntegrationActioned = (integration: ServiceOrderIntegration | Nullish) =>
  isNotNil(integration?.integration?.createdAt) || isNotNil(integration?.integration?.updatedAt);

interface IntegrationsDropdownProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isExternalActionPending?: boolean;
  isOrderReadOnly?: boolean;
}

export function IntegrationsDropdown(props: IntegrationsDropdownProps) {
  const {executeAction, isActionPending} = useServiceCaseDetailActions();
  const [hasOpened, setHasOpened] = useState(false);

  const {data, isLoading, isError, refetch} = useGetServiceOrderIntegrationsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: !hasOpened || isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId)}
  );
  const [putServiceOrderIntegration] = usePutServiceOrderIntegrationMutation();

  const {isAnyDialogOpen} = use(DialogsContext);

  const integrations = sortBy(
    (integration) => integration?.position ?? 0,
    data?.orderIntegration ?? []
  );
  const activeIntegrations = filter(isIntegrationActioned, integrations);
  const availableIntegrations = reject(isIntegrationActioned, integrations);
  const hasActiveIntegrations = isNotNilOrEmpty(activeIntegrations);
  const hasAvailableIntegrations = isNotNilOrEmpty(availableIntegrations);

  const mazdaPreauthIntegration = find(
    (integration) =>
      integration?.integration?.code === MAZDA_PORTAL_INTEGRATION_CODE &&
      integration?.integration?.serviceCode === MAZDA_PREAUTHORIZATION_SERVICE_CODE,
    integrations
  );

  const mazdaClaimIntegration = find(
    (integration) =>
      integration?.integration?.code === MAZDA_PORTAL_INTEGRATION_CODE &&
      integration?.integration?.serviceCode === MAZDA_CLAIM_SERVICE_CODE,
    integrations
  );

  const isMazdaPreauthorizationAvailable = isIntegrationActioned(mazdaPreauthIntegration);

  const onToggle = () => {
    if (isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId)) {
      return;
    }

    if (!hasOpened) {
      setHasOpened(true);
      return;
    }

    refetch();
  };

  const onActionClick = (actionKey?: IntegrationActionKey) => {
    if (isNil(actionKey)) {
      return;
    }

    const integrationActionKey = `${ORDER_INTEGRATION_ACTION_PREFIX}${actionKey}`;

    match(actionKey)
      .with('chronosContractReport', () =>
        openDialog(
          <OrderTrackedTime
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
          />,
          {title: i18n.t('entity.orderRequest.labels.workReport')}
        )
      )
      .with('audatexTaskCalculations', () =>
        openDialog(
          <AudatexCalculations
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('audatexCalculations', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.selectCalculation'),
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('silverdatMyclaimContractCalculations', () =>
        openDialog(
          <SilverdatCalculations
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('audatexCalculations', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.selectCalculation'),
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('hyundaiAs1qRetrieve', () =>
        openDialog(
          <HyundaiAs1qRetrieveDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('hyundaiAs1qRetrieve', props)}
          />,
          {
            title: i18n.t('entity.orderIntegration.actions.retrieve'),
            size: 'small',
            withAdditionalFooter: true,
          }
        )
      )
      .with('daimlerClaraClaimSend', () =>
        openDialog(
          <ClaraSendDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.daimlerClara'),
            withAdditionalFooter: true,
          }
        )
      )
      .with('daimlerClaraClaimDetail', () =>
        openDialog(
          <ClaraDetailDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.daimlerClara'),
            withAdditionalFooter: true,
          }
        )
      )
      .with('mazdaPreauthSend', () =>
        openDialog(
          <MazdaPreauthSendDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('mazdaPreauthSend', props)}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.mazdaPreauthorization'),
            withAdditionalFooter: true,
          }
        )
      )
      .with('mazdaPreauthDetail', () =>
        openDialog(
          <MazdaPreauthDetailDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            viewInMazdaUrl={mazdaPreauthIntegration?.integration?.viewDetailsLink}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('mazdaPreauthDetail', props)}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.mazdaPreauthorization'),
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('mazdaClaimSend', () =>
        openDialog(
          <MazdaClaimSendDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            isPreauthorizationAvailable={isMazdaPreauthorizationAvailable}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('mazdaClaimSend', props)}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.mazdaClaim'),
            withAdditionalFooter: true,
          }
        )
      )
      .with('mazdaClaimDetail', () =>
        openDialog(
          <MazdaClaimDetailDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            viewInMazdaUrl={mazdaClaimIntegration?.integration?.viewDetailsLink}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('mazdaClaimDetail', props)}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.mazdaClaim'),
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('mazdaChecklistChoose', () =>
        openDialog(
          <MazdaChecklistChooseDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('mazdaChecklistChoose', props)}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.mazdaChecklist'),
            size: 'small',
            withAdditionalFooter: true,
          }
        )
      )
      .otherwise((key) => {
        executeAction(integrationActionKey, () =>
          putServiceOrderIntegration({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
            body: {actionKey: key},
          })
            .unwrap()
            .then((data) => {
              if (isNotNilOrEmpty(data?.urlToOpen)) {
                openInNewTab(data?.urlToOpen!); // eslint-disable-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
              }
            })
            .catch(handleApiError)
        );
      });
  };

  return (
    <IntegrationPopper
      buttonTitle={i18n.t('entity.orderIntegration.labels.integration')}
      buttonVariant="outlined"
      buttonSize="small"
      isButtonDisabled={props.isExternalActionPending || props.isOrderReadOnly}
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmptyArray(integrations)}
      onToggle={onToggle}
      onHover={() => setHasOpened(true)}
      isOutsideClickIgnored={isAnyDialogOpen}
      data-testid={props['data-testid']}
    >
      <Show when={hasActiveIntegrations}>
        <Text color="secondary">{i18n.t('entity.integration.labels.activeIntegrations')}</Text>
        {activeIntegrations.map((integration, index) => (
          <IntegrationItem
            key={`active-${integration?.integration?.code}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
            integration={integration}
            isActionPending={isActionPending}
            onActionClick={onActionClick}
            isExternalActionPending={props.isExternalActionPending}
            data-testid={props['data-testid']}
          />
        ))}
      </Show>
      <Show when={hasAvailableIntegrations}>
        <Text color="secondary">{i18n.t('entity.integration.labels.availableIntegrations')}</Text>
        {availableIntegrations.map((integration, index) => (
          <IntegrationItem
            key={`available-${integration?.integration?.code}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
            integration={integration}
            isActionPending={isActionPending}
            onActionClick={onActionClick}
            isExternalActionPending={props.isExternalActionPending}
            data-testid={props['data-testid']}
          />
        ))}
      </Show>
    </IntegrationPopper>
  );
}
